import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Break } from 'components/AppShell';
import { Box, Divider, Typography } from '@mui/material';
import { SpeechAnalysis } from './SpeechAnalysis';

import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import InfoIconWithTooltip from 'components/InfoIconWithTooltip';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Footer from './Footer';


const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    height: '322px',
    // maxHeight : '320px',
    overflow: 'auto',
    borderRadius: theme.spacing(2, 2, 0, 0),
    border: `1px solid ${theme.palette.neutral['clr-200']}`,
    backgroundColor: theme.palette.shades['clr-white-900'],
    boxShadow: `0px 4px 4px 0px #CCD4DE1A,
                0px 10px 20px 0px #CCD4DE29,
                0px 0px 4px 0px #CCD4DE`,
    display: 'flex',
  },
  containerFullRadius: {
    borderRadius: theme.spacing(2),
  },
  score: {
    width: '260px',
    minWidth: '260px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.neutral['clr-50'],
    flexDirection: 'column',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflowX: 'auto'
  },
  navigationContainer: {
    display: 'flex',
    position: 'relative',
    width: '100%',
    height: '48px',
  },
  navigation: {
    display: 'flex',
    position: 'relative',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    borderRadius: 4,
    backgroundColor: 'white',
    alignItems: 'center',
    padding: theme.spacing(0.5),
    height: '48px',
    width: '100%',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  slider: {
    position: 'absolute',
    top: 4,
    bottom: 4,
    background: theme.palette.primary['clr-50'],
    borderRadius: 4,
    zIndex: 0,
    transform: 'translateX(0)',
    width: 0,
    height: 'calc(100% - 8px)',
    willChange: 'transform, width',
  },
  tab: {
    position: 'relative',
    zIndex: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    padding: theme.spacing(1, 2),
    whiteSpace: 'nowrap',
    height: '40px',
  },
  navArrow: {
    position: 'absolute',
    zIndex: 2,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    backgroundColor: 'white',
    top: 0,
    width: '40px',
  },
  navArrowLeft: {
    left: 0,
    borderRight: `1px solid ${theme.palette.neutral['clr-200']}`,
    boxShadow: `4px 4px 4px 0px #CCD4DE1A,
                4px 8px 20px 0px #CCD4DE1A`
  },
  navArrowRight: {
    right: 0,
    borderLeft: `1px solid ${theme.palette.neutral['clr-200']}`,
    boxShadow: `4px 4px 4px 0px #CCD4DE1A,
                4px 8px 20px 0px #CCD4DE1A`
  },
  arrowIcon: {
    fontSize: '24px',
    color: theme.palette.neutral['clr-600'],
  },
  chip: {
    border: `1px solid #1961BF`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0, 2),
    height: '23px',
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1)
  },
  improvement: {
    width: '100%',
    padding: theme.spacing(2),
    border: `2px solid ${theme.palette.primary['clr-100']}`,
    borderRadius: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  footer: {
    paddingTop: '4px', paddingBottom: '4px', borderRadius: theme.spacing(0, 0, 2, 2),
    border: `1px solid ${theme.palette.neutral['clr-200']}`,
    backgroundColor: theme.palette.shades['clr-white-900'],
    boxShadow: `0px 4px 4px 0px #CCD4DE1A,
                  0px 10px 20px 0px #CCD4DE29,
                  0px 0px 4px 0px #CCD4DE`,
  }
}));

function debounce(fn, delay) {
  let timerId;
  return function (...args) {
    if (timerId) clearTimeout(timerId);
    timerId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}

function createSharedResizeObserver(callback) {
  return new ResizeObserver(entries => {
    requestAnimationFrame(() => callback(entries));
  });
}

const FeedbackCard = ({ categories = [], maxScore = 5, isCaseStudy = false }) => {
  const classes = useStyles();

  const tabsRef = useRef({});
  const navRef = useRef({});
  const sliderElementsRef = useRef({});

  const [footerContent, setFooterContent] = useState({});

  const [state, setState] = useState({
    activeIndices: {},
    navArrowsVisible: {},
    isSmallScreen: window.innerWidth < 1100
  });

  // Case Master specific category IDs
  const caseMasterCategoryIds = ['case_introduction', 'data_analysis', 'hypothesis_testing'];

  const feedbackSectionConfig = useMemo(() => [
    { key: 'strength', title: 'Strengths', color: 'success.main' },
    { key: 'weakness', title: 'Weaknesses', color: 'error.main' },
    { key: 'improvementTips', title: 'Tips', color: 'primary.main' }
  ], []);

  const formatScore = useCallback((score) => {
    const roundedScore = Math.round(score);
    return roundedScore < 10 ? `0${roundedScore}` : `${roundedScore}`;
  }, []);

  useEffect(() => {
    const initialState = {
      activeIndices: {},
      navArrowsVisible: {},
      isSmallScreen: window.innerWidth < 1100
    };

    categories.forEach(category => {
      const categoryId = category.id;
      initialState.activeIndices[categoryId] = 0;
      initialState.navArrowsVisible[categoryId] = { showLeft: false, showRight: false };

      if (!tabsRef.current[categoryId]) {
        tabsRef.current[categoryId] = [];
      }
      if (!navRef.current[categoryId]) {
        navRef.current[categoryId] = null;
      }
      if (!sliderElementsRef.current[categoryId]) {
        sliderElementsRef.current[categoryId] = null;
      }
    });

    setState(initialState);
  }, [categories]);

  useEffect(() => {
    const checkScreenSize = () => {
      setState(prev => ({
        ...prev,
        isSmallScreen: window.innerWidth < 1100
      }));
    };

    const debouncedCheckScreenSize = debounce(checkScreenSize, 100);
    window.addEventListener('resize', debouncedCheckScreenSize);

    return () => {
      window.removeEventListener('resize', debouncedCheckScreenSize);
    };
  }, []);

  const checkScrollStatus = useCallback((categoryId) => {
    const nav = navRef.current[categoryId];
    if (!nav) return;

    let showLeft, showRight;

    if (state.isSmallScreen) {
      showLeft = true;
      showRight = true;
    } else {
      const hasHorizontalScroll = nav.scrollWidth > nav.clientWidth;
      showLeft = nav.scrollLeft > 5;
      showRight = hasHorizontalScroll &&
        Math.ceil(nav.scrollLeft) < nav.scrollWidth - nav.clientWidth - 5;
    }

    setState(prev => ({
      ...prev,
      navArrowsVisible: {
        ...prev.navArrowsVisible,
        [categoryId]: { showLeft, showRight }
      }
    }));
  }, [state.isSmallScreen]);

  const updateSliderPosition = useCallback((categoryId, index) => {
    const tabs = tabsRef.current[categoryId];
    const sliderElement = sliderElementsRef.current[categoryId];

    if (!tabs || !tabs[index] || !sliderElement) return;

    const activeTab = tabs[index];
    const { offsetLeft, offsetWidth } = activeTab;

    sliderElement.style.width = `${offsetWidth}px`;
    sliderElement.style.transform = `translateX(${offsetLeft}px)`;

    ensureTabVisible(categoryId, index);
  }, []);

  const ensureTabVisible = useCallback((categoryId, index) => {
    const nav = navRef.current[categoryId];
    const tabs = tabsRef.current[categoryId];
    if (!nav || !tabs || !tabs[index]) return;

    const tab = tabs[index];
    const navRect = nav.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();

    if (tabRect.left < navRect.left) {
      nav.scrollLeft += tabRect.left - navRect.left - 10;
    } else if (tabRect.right > navRect.right) {
      nav.scrollLeft += tabRect.right - navRect.right + 10;
    }
  }, []);

  const setActiveTab = useCallback((categoryId, index) => {
    setState(prev => ({
      ...prev,
      activeIndices: {
        ...prev.activeIndices,
        [categoryId]: index
      }
    }));

    updateSliderPosition(categoryId, index);
  }, [updateSliderPosition]);

  const scrollNav = useCallback((categoryId, direction) => {
    const nav = navRef.current[categoryId];
    if (!nav) return;

    const scrollAmount = 200;
    nav.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  }, []);

  useEffect(() => {
    const resizeObserver = createSharedResizeObserver((entries) => {
      entries.forEach(entry => {
        const categoryId = entry.target.dataset.categoryId;
        if (categoryId) {
          checkScrollStatus(categoryId);
          updateSliderPosition(categoryId, state.activeIndices[categoryId] || 0);
        }
      });
    });

    categories.forEach(category => {
      const categoryId = category.id;
      const nav = navRef.current[categoryId];

      if (nav && !nav.dataset.initialized) {
        nav.dataset.categoryId = categoryId;
        nav.dataset.initialized = "true";

        const handleScroll = debounce(() => checkScrollStatus(categoryId), 100);
        nav.addEventListener('scroll', handleScroll, { passive: true });

        resizeObserver.observe(nav);
        checkScrollStatus(categoryId);

        requestAnimationFrame(() => {
          updateSliderPosition(categoryId, state.activeIndices[categoryId] || 0);
        });
      }
    });

    return () => {
      resizeObserver.disconnect();

      categories.forEach(category => {
        const categoryId = category.id;
        const nav = navRef.current[categoryId];

        if (nav && nav.dataset.initialized) {
          nav.removeEventListener('scroll', () => checkScrollStatus(categoryId));
        }
      });
    };
  }, [categories, state.activeIndices, checkScrollStatus, updateSliderPosition]);

  useEffect(() => {
    Object.entries(state.activeIndices).forEach(([categoryId, index]) => {
      updateSliderPosition(categoryId, index);
    });
  }, [state.activeIndices, updateSliderPosition]);

  const renderFeedbackContent = useCallback((content, type) => {
    if (type === 'improvementTips' && Array.isArray(content)) {
      return (
        <Box component="ul" sx={{ pl: 5, mb: 0, mt: 0 }}>
          {content.map((tip, idx) => (
            <Box component="li" key={idx} sx={{ mb: 1 }}>
              <Typography variant="body01-medium" component="span" color="text.primary">
                {tip}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Typography variant="body01-medium" color="text.primary">
        {content}
      </Typography>
    );
  }, []);

  React.useEffect(() => {
    const footerContent = {};

    // Only add speech disclaimer if not case study
    if (!isCaseStudy) {
      footerContent['speech'] = [{
        content: 'Speech score is not a part of the 4C. It is provided solely for your reference.',
        type: 'default',
        hide: false
      }];
    }

    categories.forEach(({ subCategories, id }) => {
      subCategories.forEach(({ label, hintUsedCount }) => {
        if (!hintUsedCount) return;
        footerContent[id] = [
          ...(footerContent[id] || []),
          {
            content: `*Mark(s) deduction of -${hintUsedCount} from ${label} because your hint was related to it.`,
            type: 'error',
            hide: false
          }]
      });

    });

    setFooterContent(footerContent);
  }, [categories, isCaseStudy]);

  return categories.map(({ parentLabel, score, description, id, imgSrc, label, subCategories }) => {
    const categoryActiveIndex = state.activeIndices[id] || 0;
    const activeSubCategory = subCategories[categoryActiveIndex] || {};
    const categoryArrows = state.navArrowsVisible[id] || { showLeft: false, showRight: false };

    // Check if footer has content for this category
    const hasFooterContent = footerContent?.[id] && footerContent[id].length > 0;

    return (
      <React.Fragment key={id}>
        {parentLabel && (
          <Typography variant="h4-medium" color="neutral.clr-900" id={parentLabel}>
            {parentLabel}
          </Typography>
        )}

        <Break value={5} />

        <Box className={`${classes.container} ${!hasFooterContent ? classes.containerFullRadius : ''}`}>
          <Box className={classes.score}>
            <img
              src={imgSrc}
              alt={label}
              width={caseMasterCategoryIds.includes(id) ? 80 : 148}
              style={caseMasterCategoryIds.includes(id) ? {
                paddingBottom:'20px'
              } : {
                mixBlendMode: 'color-burn'
              }}
            />
            <Box display="flex" flexDirection="column" gap="4px" alignItems="center">
              <Box display="inline-flex" gap="4px" alignItems="center" flexWrap="wrap" justifyContent="center" maxWidth="260px">
                <Typography
                  component="span"
                  // Case study parameter names are long enough to wrap in the
                  // 260px column, so they step down one stop on the type scale.
                  variant={isCaseStudy ? 'h6-semiBold' : 'h5-semiBold'}
                  color="neutral.clr-600"
                  sx={{ textAlign: 'center', maxWidth: '240px', display: 'inline' }}
                >
                  {label}
                </Typography>
                {description && (
                  <Box component="span" display="inline-flex" alignItems="center">
                    <InfoIconWithTooltip title={description} />
                  </Box>
                )}
              </Box>
              <Typography
                variant={isCaseStudy ? 'body02-bold' : 'body01-bold'}
                color="neutral.clr-600"
              >
                Score: {formatScore(score)}/{maxScore}
              </Typography>
            </Box>
          </Box>
          <Box className={classes.content}>
            <Box className={classes.navigationContainer}>
              {categoryArrows.showLeft && (
                <Box
                  className={`${classes.navArrow} ${classes.navArrowLeft}`}
                  onClick={() => scrollNav(id, 'left')}
                >
                  <ArrowBackIosIcon sx={{ fontSize: '12px', color: '#1961BF' }} />
                </Box>
              )}

              <Box
                className={classes.navigation}
                ref={(el) => {
                  navRef.current[id] = el;
                }}
                sx={{
                  scrollBehavior: 'smooth',
                  ml: categoryArrows.showLeft ? '40px' : 1,
                  mr: categoryArrows.showRight ? '40px' : 1
                }}
              >
                <Box
                  className={classes.slider}
                  ref={(el) => {
                    sliderElementsRef.current[id] = el;
                  }}
                />
                {subCategories.map(({ label, score, description, hintUsedCount }, index) => (
                  <React.Fragment key={index}>
                    <Box
                      className={classes.tab}
                      ref={(el) => {
                        if (!tabsRef.current[id]) {
                          tabsRef.current[id] = [];
                        }
                        tabsRef.current[id][index] = el;
                      }}
                      onClick={() => setActiveTab(id, index)}
                      sx={{
                        backgroundColor: categoryActiveIndex !== index ? '#F8F9FA' : 'transparent',
                        '&:hover': {
                          backgroundColor: categoryActiveIndex !== index ? '#F5F9FF' : 'transparent',
                        },
                      }}
                    >
                      <Typography
                        variant="h6-semiBold"
                        color={categoryActiveIndex === index ? 'primary.main' : 'neutral.clr-800'}
                      >
                        {label}:&nbsp;

                        <span style={{ color: hintUsedCount ? '#E2483C' : 'inherit' }}>
                          {formatScore(score)}
                        </span>

                      </Typography>
                      <InfoIconWithTooltip
                        sx={{ fontSize: '18px', marginTop: '2px' }} placement="top"
                        title={description}
                      />
                    </Box>
                    {index < subCategories.length - 1 && (
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ borderColor: '#CCD4DE', mx: 1 }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </Box>

              {categoryArrows.showRight && (
                <Box
                  className={`${classes.navArrow} ${classes.navArrowRight}`}
                  onClick={() => scrollNav(id, 'right')}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: '12px', color: '#1961BF' }} />
                </Box>
              )}
            </Box>

            {
              (id === 'speech') ?
                <SpeechAnalysis subCategory={activeSubCategory} />
                :
                <Box display="flex" borderTop="1px solid #E0E0E0" height='100%'>

                  {
                    feedbackSectionConfig.map(({ key, title, color }, index) => (
                      <Box
                        key={title}
                        flex={1}
                        px={3}
                        py={3}
                        borderRight={index < feedbackSectionConfig.length - 1 ? '1px solid #E0E0E0' : 'none'}
                      >
                        <Typography
                          variant='h6-semiBold'
                          color={color}
                        >
                          {title}
                        </Typography>
                        <Divider sx={{ mb: 2, borderColor: '#E0E0E0' }} />
                        <Box sx={{ height: '100%', maxHeight: '215px', width: '100%', overflow: 'auto' }}>
                          {activeSubCategory[key] && renderFeedbackContent(activeSubCategory[key], key)}
                        </Box>
                      </Box>
                    ))
                  }

                </Box>
            }
          </Box>
        </Box>

        {hasFooterContent && (
          <Footer
            items={footerContent?.[id] || []}
            className={classes.footer}
          />
        )}

        <Break value={5} />

      </React.Fragment>
    );
  });
};

export default FeedbackCard;